import { Fragment, type ReactNode } from 'react';

// Minimal, dependency-free renderer for the markdown subset the assistant emits
// (headings, bullet/numbered lists with nesting, bold, italic, inline code).
// Builds React nodes only, so no HTML is ever injected.

const INLINE = /(\*\*[^*\n]+\*\*|__[^_\n]+__|`[^`\n]+`|\*[^*\s][^*\n]*\*)/g;

function renderInline(text: string): ReactNode[] {
  return text.split(INLINE).filter(Boolean).map((part, i) => {
    if ((part.startsWith('**') && part.endsWith('**') && part.length > 4) ||
        (part.startsWith('__') && part.endsWith('__') && part.length > 4)) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={i} className="rounded bg-background/60 px-1 py-0.5 text-xs">{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'para'; text: string }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'item'; text: string; depth: number; ordered: boolean; marker: string };

function parse(src: string): Block[] {
  const blocks: Block[] = [];
  const lines = src.replace(/\r/g, '').split('\n');
  const cells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const isRule = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim()) continue;
    if (raw.trim().startsWith('|')) {
      const tbl: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) tbl.push(lines[i++]);
      i--;
      const body = tbl.filter((l) => !isRule(l)).map(cells);
      if (body.length) blocks.push({ kind: 'table', header: body[0], rows: body.slice(1) });
      continue;
    }
    const heading = raw.match(/^\s*#{1,6}\s+(.*)$/);
    if (heading) { blocks.push({ kind: 'heading', text: heading[1].replace(/\*\*/g, '') }); continue; }
    const item = raw.match(/^(\s*)([-*•]|\d+[.)])\s+(.*)$/);
    if (item) {
      const ordered = /\d/.test(item[2]);
      blocks.push({
        kind: 'item', text: item[3], ordered, marker: item[2],
        depth: Math.min(Math.floor(item[1].replace(/\t/g, '  ').length / 2), 3),
      });
      continue;
    }
    blocks.push({ kind: 'para', text: raw.trim() });
  }
  return blocks;
}

export function ChatMarkdown({ text }: { text: string }) {
  const blocks = parse(text);
  return (
    <div dir="auto" className="space-y-1.5 break-words">
      {blocks.map((b, i) => {
        if (b.kind === 'heading') {
          return <p key={i} dir="auto" className={`font-semibold ${i > 0 ? 'pt-1.5' : ''}`}>{renderInline(b.text)}</p>;
        }
        if (b.kind === 'table') {
          return (
            <div key={i} className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>{b.header.map((h, j) => (
                    <th key={j} className="border-b border-border px-2 py-1 text-start font-semibold">{renderInline(h)}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {b.rows.map((r, j) => (
                    <tr key={j}>{r.map((c, k) => (
                      <td key={k} className="border-b border-border/50 px-2 py-1 align-top">{renderInline(c)}</td>
                    ))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (b.kind === 'item') {
          return (
            <div key={i} dir="auto" className="flex gap-2" style={{ paddingInlineStart: b.depth * 14 }}>
              <span className="shrink-0 text-muted-foreground" aria-hidden>
                {b.ordered ? b.marker : b.depth > 0 ? '–' : '•'}
              </span>
              <span className="min-w-0">{renderInline(b.text)}</span>
            </div>
          );
        }
        return <p key={i} dir="auto">{renderInline(b.text)}</p>;
      })}
    </div>
  );
}
